import { palette } from '@kimuno/theme';
import { Box, SvgIcon, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import getMuiIcon from '../../../utils/getMuiIcon';

interface MuLogoProps {
	onClick?: () => void;
	variant?: 'full' | 'small';
	sx?: SystemStyleObject<Theme>;
}

const getLogoContainerStyles = (
	isFull: boolean,
	onClick?: () => void
): SystemStyleObject<Theme> => ({
	padding: isFull ? '24px 0 24px 24px' : 0,
	cursor: onClick ? 'pointer' : 'default',
	display: 'flex',
	alignItems: 'center',
	justifyContent: isFull ? 'flex-start' : 'center',
});

const MuLogo = ({ onClick, variant = 'full', sx = {} }: MuLogoProps) => {
	const { t } = useTranslation();
	const isFull = variant === 'full';
	const logoProps = isFull
		? {
				width: '102px',
				height: '16px',
				viewBox: '0 0 128 20',
			}
		: {
				width: '24px',
				height: '24px',
				viewBox: '0 0 32 32',
			};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (!onClick) return;
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onClick();
		}
	};

	return (
		<Box
			data-testid={isFull ? 'logo-button-full' : 'logo-button-small'}
			onClick={onClick}
			onKeyDown={onClick ? handleKeyDown : undefined}
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
			aria-label={onClick ? t('Home') : undefined}
			sx={{ ...getLogoContainerStyles(isFull, onClick), ...sx }}
		>
			<SvgIcon
				sx={{
					width: logoProps.width,
					height: logoProps.height,
					color: palette.primary.main,
				}}
				component={getMuiIcon(isFull ? 'muLogoFull' : 'muLogoBag')}
				viewBox={logoProps.viewBox}
			/>
		</Box>
	);
};

export default MuLogo;
